import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  GraduationCap, Search, Filter, Eye, Calendar, MapPin, Users, 
  Phone, Mail, Building, CheckCircle, Send, FileText, Clock,
  RefreshCw, ChevronDown, ChevronUp, ExternalLink, Download, Share2, 
  ClipboardCheck, UserCheck, Star, Award
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed', labelAr: 'مؤكد', color: 'bg-green-100 text-green-800' },
  { value: 'tna_sent', label: 'TNA Sent', labelAr: 'تم إرسال TNA', color: 'bg-blue-100 text-blue-800' },
  { value: 'tna_received', label: 'TNA Received', labelAr: 'تم استلام TNA', color: 'bg-purple-100 text-purple-800' },
  { value: 'ready_for_delivery', label: 'Ready for Delivery', labelAr: 'جاهز للتنفيذ', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'trainer_eval_sent', label: 'Trainer Eval Sent', labelAr: 'تم إرسال تقييم المدرب', color: 'bg-orange-100 text-orange-800' },
  { value: 'trainer_eval_completed', label: 'Trainer Eval Done', labelAr: 'تم تقييم المدرب', color: 'bg-teal-100 text-teal-800' },
  { value: 'trainee_eval_sent', label: 'Trainee Eval Sent', labelAr: 'تم إرسال تقييم المتدرب', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'closed', label: 'Closed', labelAr: 'مغلق', color: 'bg-gray-100 text-gray-800' },
];

export default function AdminConfirmedEnrollmentsPage() {
  const { language } = useLanguage();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resending, setResending] = useState(false);
  const [showTNA, setShowTNA] = useState(false);
  const [showEvaluations, setShowEvaluations] = useState(false);
  const [sendingTrainerEval, setSendingTrainerEval] = useState(false);
  const [sendingTraineeEval, setSendingTraineeEval] = useState(false);
  const [sharingTNA, setSharingTNA] = useState(false);
  const [trainerEmail, setTrainerEmail] = useState('');
  const [tnaShareEmail, setTnaShareEmail] = useState('');
  const [evaluations, setEvaluations] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    admin_notes: ''
  });

  useEffect(() => {
    fetchEnrollments();
  }, [statusFilter]);

  const fetchEnrollments = async () => {
    try {
      const url = statusFilter && statusFilter !== 'all' 
        ? `${API}/admin/confirmed-enrollments?status=${statusFilter}`
        : `${API}/admin/confirmed-enrollments`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEnrollments(data);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (enrollment) => {
    try {
      const response = await fetch(`${API}/admin/confirmed-enrollments/${enrollment.enrollment_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedEnrollment(data);
        setEditForm({
          status: data.status,
          admin_notes: data.admin_notes || ''
        });
        setShowTNA(false);
        setShowEvaluations(false);
        setEvaluations(null);
        setTrainerEmail('');
        setTnaShareEmail('');
        setDetailDialogOpen(true);
        
        // Load evaluations if available
        if (data.trainer_eval_id || data.trainee_eval_id) {
          fetchEvaluations(enrollment.enrollment_id);
        }
      }
    } catch (error) {
      toast.error('Failed to load enrollment details');
    }
  };

  const fetchEvaluations = async (enrollmentId) => {
    try {
      const response = await fetch(`${API}/admin/confirmed-enrollments/${enrollmentId}/evaluations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEvaluations(data);
      }
    } catch (error) {
      console.error('Failed to fetch evaluations:', error);
    }
  };

  const handleUpdateEnrollment = async () => {
    if (!selectedEnrollment) return;
    setUpdating(true);
    
    try {
      const response = await fetch(`${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success('Enrollment updated');
        setDetailDialogOpen(false);
        fetchEnrollments();
      } else {
        toast.error('Failed to update enrollment');
      }
    } catch (error) {
      toast.error('Failed to update enrollment');
    } finally {
      setUpdating(false);
    }
  };

  const handleResendTNA = async () => {
    if (!selectedEnrollment) return;
    setResending(true);
    
    try {
      const response = await fetch(`${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/resend-tna`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('TNA email resent successfully');
        fetchEnrollments();
      } else {
        toast.error('Failed to resend TNA email');
      }
    } catch (error) {
      toast.error('Failed to resend TNA email');
    } finally {
      setResending(false);
    }
  };

  const handleShareTNA = async () => {
    if (!selectedEnrollment) return;
    setSharingTNA(true);
    
    try {
      const url = tnaShareEmail 
        ? `${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/share-tna?recipient_email=${encodeURIComponent(tnaShareEmail)}`
        : `${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/share-tna`;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(tnaShareEmail ? `TNA summary sent to ${tnaShareEmail}` : 'TNA summary sent to admin email');
        setTnaShareEmail('');
      } else {
        toast.error('Failed to send TNA summary');
      }
    } catch (error) {
      toast.error('Failed to send TNA summary');
    } finally {
      setSharingTNA(false);
    }
  };

  const handleExportTNA = async () => {
    if (!selectedEnrollment) return;
    
    try {
      const response = await fetch(`${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/tna-export`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Generate printable HTML content
        const printContent = generateTNAPrintContent(data);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      } else {
        toast.error('Failed to export TNA');
      }
    } catch (error) {
      toast.error('Failed to export TNA');
    }
  };

  const generateTNAPrintContent = (data) => {
    const { enrollment, tna, company_info } = data;
    const levelLabels = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' };
    const langLabels = { english: 'English', arabic: 'Arabic', bilingual: 'Bilingual' };
    const materialLabels = { yes: 'Yes - Arabic Only', no: 'No - English Only', both: 'Both Arabic and English' };
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>TNA Report - ${enrollment.full_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #33AADD; padding-bottom: 20px; }
          .header h1 { color: #33AADD; margin: 0; }
          .header p { color: #666; margin: 5px 0; }
          .section { margin-bottom: 25px; }
          .section h2 { color: #111827; font-size: 16px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 15px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .info-item { padding: 8px; background: #f9fafb; border-radius: 4px; }
          .info-item label { font-weight: bold; color: #374151; display: block; font-size: 12px; }
          .info-item span { color: #111827; }
          .response-item { margin-bottom: 15px; }
          .response-item label { font-weight: bold; color: #374151; display: block; margin-bottom: 5px; }
          .response-item p { margin: 0; padding: 10px; background: #f9fafb; border-radius: 4px; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #666; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>WeDo HR Services</h1>
          <p>Training Needs Assessment Report</p>
        </div>
        
        <div class="section">
          <h2>Course Information</h2>
          <div class="info-grid">
            <div class="info-item"><label>Course</label><span>${enrollment.course_title}</span></div>
            <div class="info-item"><label>Location</label><span>${enrollment.city}</span></div>
            <div class="info-item"><label>Dates</label><span>${enrollment.start_date} - ${enrollment.end_date}</span></div>
            <div class="info-item"><label>Delivery Mode</label><span>${enrollment.delivery_mode}</span></div>
          </div>
        </div>
        
        <div class="section">
          <h2>Participant Information</h2>
          <div class="info-grid">
            <div class="info-item"><label>Name</label><span>${enrollment.full_name}</span></div>
            <div class="info-item"><label>Email</label><span>${enrollment.email}</span></div>
            <div class="info-item"><label>Company</label><span>${enrollment.company || 'N/A'}</span></div>
            <div class="info-item"><label>Participants</label><span>${enrollment.num_participants}</span></div>
          </div>
        </div>
        
        <div class="section">
          <h2>Training Needs Assessment Responses</h2>
          <div class="response-item">
            <label>Overall Objective</label>
            <p>${tna.overall_objective}</p>
          </div>
          <div class="response-item">
            <label>Topics to Emphasize</label>
            <p>${tna.topics_to_emphasize || 'None specified'}</p>
          </div>
          <div class="response-item">
            <label>Topics to Reduce/Skip</label>
            <p>${tna.topics_to_reduce || 'None specified'}</p>
          </div>
          <div class="info-grid">
            <div class="info-item"><label>Participant Level</label><span>${levelLabels[tna.participant_level] || tna.participant_level}</span></div>
            <div class="info-item"><label>Preferred Language</label><span>${langLabels[tna.preferred_language] || tna.preferred_language}</span></div>
            <div class="info-item"><label>Materials in Arabic</label><span>${materialLabels[tna.materials_in_arabic] || tna.materials_in_arabic}</span></div>
          </div>
          <div class="response-item" style="margin-top: 15px;">
            <label>Preferred Case Studies</label>
            <p>${tna.preferred_case_studies || 'None specified'}</p>
          </div>
          <div class="response-item">
            <label>Additional Notes</label>
            <p>${tna.additional_notes || 'None'}</p>
          </div>
        </div>
        
        <div class="footer">
          <p>${company_info.name} | ${company_info.address}</p>
          <p>${company_info.email} | ${company_info.phone}</p>
          <p>Submitted: ${tna.submitted_at ? tna.submitted_at.substring(0, 10) : 'N/A'}</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleSendTrainerEval = async () => {
    if (!selectedEnrollment) return;
    setSendingTrainerEval(true);
    
    try {
      const url = trainerEmail 
        ? `${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/send-trainer-eval?trainer_email=${encodeURIComponent(trainerEmail)}`
        : `${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/send-trainer-eval`;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setTrainerEmail('');
        // Refresh to get updated status
        handleViewDetails(selectedEnrollment);
        fetchEnrollments();
      } else {
        toast.error('Failed to send trainer evaluation');
      }
    } catch (error) {
      toast.error('Failed to send trainer evaluation');
    } finally {
      setSendingTrainerEval(false);
    }
  };

  const handleSendTraineeEval = async () => {
    if (!selectedEnrollment) return;
    setSendingTraineeEval(true);
    
    try {
      const response = await fetch(`${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/send-trainee-eval`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh to get updated status
        handleViewDetails(selectedEnrollment);
        fetchEnrollments();
      } else {
        toast.error('Failed to send trainee evaluation');
      }
    } catch (error) {
      toast.error('Failed to send trainee evaluation');
    } finally {
      setSendingTraineeEval(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status);
    if (!statusOption) return <Badge>{status}</Badge>;
    return (
      <Badge className={statusOption.color}>
        {language === 'ar' ? statusOption.labelAr : statusOption.label}
      </Badge>
    );
  };

  const getTNABadge = (enrollment) => {
    if (enrollment.tna_completed) {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Received</Badge>;
    } else if (enrollment.tna_sent) {
      return <Badge className="bg-blue-100 text-blue-800"><Send className="h-3 w-3 mr-1" /> Sent</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-600"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const getEvalBadge = (enrollment) => {
    if (enrollment.trainee_eval_completed) {
      return <Badge className="bg-green-100 text-green-800"><Star className="h-3 w-3 mr-1" /> Complete</Badge>;
    } else if (enrollment.trainee_eval_sent) {
      return <Badge className="bg-indigo-100 text-indigo-800"><Send className="h-3 w-3 mr-1" /> Trainee Sent</Badge>;
    } else if (enrollment.trainer_eval_completed) {
      return <Badge className="bg-teal-100 text-teal-800"><UserCheck className="h-3 w-3 mr-1" /> Trainer Done</Badge>;
    } else if (enrollment.trainer_eval_sent) {
      return <Badge className="bg-orange-100 text-orange-800"><Send className="h-3 w-3 mr-1" /> Trainer Sent</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-600"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const getRatingLabel = (value) => {
    const labels = { 1: 'Poor', 2: 'Below Avg', 3: 'Average', 4: 'Good', 5: 'Great' };
    return labels[value] || value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredEnrollments = enrollments.filter(enr => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      enr.full_name?.toLowerCase().includes(term) ||
      enr.email?.toLowerCase().includes(term) ||
      enr.company?.toLowerCase().includes(term) ||
      enr.course_title?.toLowerCase().includes(term) ||
      enr.city?.toLowerCase().includes(term)
    );
  });

  // Stats
  const stats = {
    total: enrollments.length,
    confirmed: enrollments.filter(e => e.status === 'confirmed').length,
    tnaSent: enrollments.filter(e => e.status === 'tna_sent').length,
    tnaReceived: enrollments.filter(e => e.status === 'tna_received').length,
    readyForDelivery: enrollments.filter(e => e.status === 'ready_for_delivery').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {language === 'ar' ? 'التسجيلات المؤكدة' : 'Confirmed Enrollments'}
          </h1>
          <p className="text-[#6B7280]">
            {language === 'ar' ? 'إدارة التسجيلات المؤكدة وتتبع TNA' : 'Manage confirmed enrollments and TNA tracking'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#33AADD]/10 flex items-center justify-center">
                <GraduationCap className="h-5 w-5 text-[#33AADD]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.total}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'إجمالي' : 'Total'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.confirmed}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'مؤكد' : 'Confirmed'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Send className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.tnaSent}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'TNA مرسل' : 'TNA Sent'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.tnaReceived}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'TNA مستلم' : 'TNA Received'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.readyForDelivery}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'جاهز' : 'Ready'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                <Input
                  placeholder={language === 'ar' ? 'بحث...' : 'Search by name, email, company, course...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={language === 'ar' ? 'الحالة' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'ar' ? 'جميع الحالات' : 'All Statuses'}</SelectItem>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {language === 'ar' ? status.labelAr : status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Enrollments Table */}
      <Card>
        <CardContent className="p-0">
          {filteredEnrollments.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="h-12 w-12 text-[#D1D5DB] mx-auto mb-4" />
              <p className="text-[#6B7280]">
                {language === 'ar' ? 'لا توجد تسجيلات مؤكدة' : 'No confirmed enrollments found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-[#F9FAFB]">
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'الدورة' : 'Course'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'المشارك' : 'Participant'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'التاريخ' : 'Dates'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">TNA</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'التقييم' : 'Eval'}</th>
                    <th className="text-right p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'إجراءات' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnrollments.map((enr) => (
                    <tr key={enr.enrollment_id} className="border-b hover:bg-[#F9FAFB]">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[#111827] line-clamp-1">{enr.course_title}</p>
                          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                            <MapPin className="h-3 w-3" />
                            <span>{enr.city}</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[#111827]">{enr.full_name}</p>
                          <p className="text-sm text-[#6B7280]">{enr.email}</p>
                          {enr.company && <p className="text-sm text-[#9CA3AF]">{enr.company}</p>}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p>{formatDate(enr.start_date)}</p>
                          <p className="text-[#6B7280]">{formatDate(enr.end_date)}</p>
                        </div>
                      </td>
                      <td className="p-4">{getStatusBadge(enr.status)}</td>
                      <td className="p-4">{getTNABadge(enr)}</td>
                      <td className="p-4">{getEvalBadge(enr)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(enr)}
                            data-testid={`view-enrollment-${enr.enrollment_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'تفاصيل التسجيل' : 'Enrollment Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEnrollment && (
            <div className="space-y-6">
              {/* Course Info */}
              <div className="p-4 bg-[#F0F9FF] rounded-lg">
                <h3 className="font-semibold text-[#111827] mb-2">{selectedEnrollment.course_title}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#33AADD]" />
                    <span>{selectedEnrollment.city}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#33AADD]" />
                    <span>{formatDate(selectedEnrollment.start_date)} - {formatDate(selectedEnrollment.end_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#33AADD]" />
                    <span>{selectedEnrollment.num_participants || 1} participant(s)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">EUR {selectedEnrollment.price?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Participant Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'الاسم' : 'Name'}</Label>
                  <p className="font-medium">{selectedEnrollment.full_name}</p>
                </div>
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'البريد' : 'Email'}</Label>
                  <p className="font-medium">{selectedEnrollment.email}</p>
                </div>
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'الهاتف' : 'Phone'}</Label>
                  <p className="font-medium">{selectedEnrollment.phone}</p>
                </div>
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'الشركة' : 'Company'}</Label>
                  <p className="font-medium">{selectedEnrollment.company || '-'}</p>
                </div>
              </div>

              {/* TNA Section */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827]">Training Needs Assessment</h4>
                  <div className="flex items-center gap-2">
                    {getTNABadge(selectedEnrollment)}
                  </div>
                </div>
                
                {/* TNA Form Link */}
                <div className="mb-4 p-3 bg-[#F0F9FF] rounded-lg border border-[#33AADD]/20">
                  <Label className="text-[#6B7280] text-xs block mb-1">TNA Form Link (share with participant)</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-white p-2 rounded border text-[#111827] truncate">
                      {`${window.location.origin}/tna/${selectedEnrollment.enrollment_id}`}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/tna/${selectedEnrollment.enrollment_id}`);
                        toast.success('TNA link copied!');
                      }}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/tna/${selectedEnrollment.enrollment_id}`, '_blank')}
                      data-testid="open-tna-form"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open Form
                    </Button>
                  </div>
                </div>
                
                {/* TNA Actions */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {!selectedEnrollment.tna_completed && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleResendTNA}
                      disabled={resending}
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${resending ? 'animate-spin' : ''}`} />
                      {resending ? 'Sending...' : 'Resend TNA Email'}
                    </Button>
                  )}
                  
                  {selectedEnrollment.tna_completed && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleExportTNA}
                        data-testid="export-tna-pdf"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Export PDF
                      </Button>
                      <div className="flex items-center gap-1">
                        <Input
                          placeholder="Email to share..."
                          value={tnaShareEmail}
                          onChange={(e) => setTnaShareEmail(e.target.value)}
                          className="h-8 w-40 text-sm"
                        />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleShareTNA}
                          disabled={sharingTNA}
                          data-testid="share-tna-email"
                        >
                          <Share2 className={`h-4 w-4 mr-1 ${sharingTNA ? 'animate-pulse' : ''}`} />
                          {sharingTNA ? 'Sending...' : 'Share'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                
                {selectedEnrollment.tna_sent && (
                  <p className="text-sm text-[#6B7280] mb-2">
                    TNA sent: {formatDate(selectedEnrollment.tna_sent_at)}
                  </p>
                )}
                
                {selectedEnrollment.tna_completed && selectedEnrollment.tna_response && (
                  <div className="mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowTNA(!showTNA)}
                      className="w-full justify-between"
                    >
                      <span>View TNA Response</span>
                      {showTNA ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    
                    {showTNA && (
                      <div className="mt-4 space-y-4 p-4 bg-[#F9FAFB] rounded-lg">
                        <div>
                          <Label className="text-[#6B7280] text-sm">Overall Objective</Label>
                          <p className="text-[#111827]">{selectedEnrollment.tna_response.overall_objective}</p>
                        </div>
                        {selectedEnrollment.tna_response.topics_to_emphasize && (
                          <div>
                            <Label className="text-[#6B7280] text-sm">Topics to Emphasize</Label>
                            <p className="text-[#111827]">{selectedEnrollment.tna_response.topics_to_emphasize}</p>
                          </div>
                        )}
                        {selectedEnrollment.tna_response.topics_to_reduce && (
                          <div>
                            <Label className="text-[#6B7280] text-sm">Topics to Reduce/Skip</Label>
                            <p className="text-[#111827]">{selectedEnrollment.tna_response.topics_to_reduce}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label className="text-[#6B7280] text-sm">Participant Level</Label>
                            <p className="text-[#111827] capitalize">{selectedEnrollment.tna_response.participant_level}</p>
                          </div>
                          <div>
                            <Label className="text-[#6B7280] text-sm">Preferred Language</Label>
                            <p className="text-[#111827] capitalize">{selectedEnrollment.tna_response.preferred_language}</p>
                          </div>
                          <div>
                            <Label className="text-[#6B7280] text-sm">Materials in Arabic</Label>
                            <p className="text-[#111827] capitalize">{selectedEnrollment.tna_response.materials_in_arabic}</p>
                          </div>
                        </div>
                        {selectedEnrollment.tna_response.preferred_case_studies && (
                          <div>
                            <Label className="text-[#6B7280] text-sm">Preferred Case Studies</Label>
                            <p className="text-[#111827]">{selectedEnrollment.tna_response.preferred_case_studies}</p>
                          </div>
                        )}
                        {selectedEnrollment.tna_response.additional_notes && (
                          <div>
                            <Label className="text-[#6B7280] text-sm">Additional Notes</Label>
                            <p className="text-[#111827]">{selectedEnrollment.tna_response.additional_notes}</p>
                          </div>
                        )}
                        <p className="text-xs text-[#9CA3AF]">
                          Submitted: {formatDate(selectedEnrollment.tna_response.submitted_at)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Post-Delivery Evaluations Section */}
              <div className="border rounded-lg p-4 border-emerald-200 bg-emerald-50/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-emerald-600" />
                    Post-Delivery Evaluations
                  </h4>
                  <div className="flex items-center gap-2">
                    {getEvalBadge(selectedEnrollment)}
                  </div>
                </div>

                {/* Trainer Evaluation */}
                <div className="mb-4 p-3 bg-white rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-medium flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-orange-600" />
                      Trainer Evaluation (Trainer → Trainee)
                    </Label>
                    {selectedEnrollment.trainer_eval_completed ? (
                      <Badge className="bg-green-100 text-green-800">Completed</Badge>
                    ) : selectedEnrollment.trainer_eval_sent ? (
                      <Badge className="bg-orange-100 text-orange-800">Sent</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600">Not Sent</Badge>
                    )}
                  </div>
                  
                  {!selectedEnrollment.trainer_eval_completed && (
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        placeholder="Trainer email (optional)"
                        value={trainerEmail}
                        onChange={(e) => setTrainerEmail(e.target.value)}
                        className="h-8 flex-1 text-sm"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleSendTrainerEval}
                        disabled={sendingTrainerEval}
                        data-testid="send-trainer-eval"
                      >
                        <Send className={`h-4 w-4 mr-1 ${sendingTrainerEval ? 'animate-pulse' : ''}`} />
                        {sendingTrainerEval ? 'Sending...' : 'Send Eval'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/trainer-evaluation/${selectedEnrollment.enrollment_id}`, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {selectedEnrollment.trainer_eval_sent && (
                    <p className="text-xs text-[#6B7280] mt-2">
                      Sent: {formatDate(selectedEnrollment.trainer_eval_sent_at)}
                    </p>
                  )}
                </div>

                {/* Trainee/Course Evaluation */}
                <div className="p-3 bg-white rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-medium flex items-center gap-2">
                      <Star className="h-4 w-4 text-indigo-600" />
                      Course Evaluation (Trainee → Course & Trainer)
                    </Label>
                    {selectedEnrollment.trainee_eval_completed ? (
                      <Badge className="bg-green-100 text-green-800">Completed</Badge>
                    ) : selectedEnrollment.trainee_eval_sent ? (
                      <Badge className="bg-indigo-100 text-indigo-800">Sent</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600">Not Sent</Badge>
                    )}
                  </div>
                  
                  {!selectedEnrollment.trainee_eval_completed && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm text-[#6B7280] flex-1">Will be sent to: {selectedEnrollment.email}</span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleSendTraineeEval}
                        disabled={sendingTraineeEval}
                        data-testid="send-trainee-eval"
                      >
                        <Send className={`h-4 w-4 mr-1 ${sendingTraineeEval ? 'animate-pulse' : ''}`} />
                        {sendingTraineeEval ? 'Sending...' : 'Send Eval'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/course-evaluation/${selectedEnrollment.enrollment_id}`, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {selectedEnrollment.trainee_eval_sent && (
                    <p className="text-xs text-[#6B7280] mt-2">
                      Sent: {formatDate(selectedEnrollment.trainee_eval_sent_at)}
                    </p>
                  )}
                </div>

                {/* View Evaluations Button */}
                {(selectedEnrollment.trainer_eval_completed || selectedEnrollment.trainee_eval_completed) && (
                  <div className="mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowEvaluations(!showEvaluations)}
                      className="w-full justify-between"
                    >
                      <span>View Evaluation Results</span>
                      {showEvaluations ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    
                    {showEvaluations && evaluations && (
                      <div className="mt-4 space-y-4">
                        {/* Trainer Evaluation Results */}
                        {evaluations.trainer_evaluation && (
                          <div className="p-4 bg-orange-50 rounded-lg">
                            <h5 className="font-medium text-[#111827] mb-3 flex items-center gap-2">
                              <UserCheck className="h-4 w-4 text-orange-600" />
                              Trainer Evaluation Results
                            </h5>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="flex justify-between p-2 bg-white rounded">
                                <span>Evaluated by:</span>
                                <span className="font-medium">{evaluations.trainer_evaluation.trainer_name}</span>
                              </div>
                              <div className="flex justify-between p-2 bg-white rounded">
                                <span>Average Score:</span>
                                <span className="font-bold text-orange-600">{evaluations.trainer_evaluation.average_score}/5</span>
                              </div>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                              {['participation', 'attitude', 'professionalism', 'communication_skills', 
                                'sense_of_responsibility', 'readiness_for_initiations', 'focus_ability', 
                                'openness_to_feedback', 'listening_skills', 'enthusiasm'].map(key => (
                                <div key={key} className="flex justify-between p-1">
                                  <span className="text-[#6B7280] capitalize">{key.replace(/_/g, ' ')}:</span>
                                  <span>{getRatingLabel(evaluations.trainer_evaluation[key])}</span>
                                </div>
                              ))}
                            </div>
                            {evaluations.trainer_evaluation.comments && (
                              <div className="mt-3 p-2 bg-white rounded">
                                <Label className="text-xs text-[#6B7280]">Comments:</Label>
                                <p className="text-sm">{evaluations.trainer_evaluation.comments}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Trainee/Course Evaluation Results */}
                        {evaluations.trainee_evaluation && (
                          <div className="p-4 bg-indigo-50 rounded-lg">
                            <h5 className="font-medium text-[#111827] mb-3 flex items-center gap-2">
                              <Star className="h-4 w-4 text-indigo-600" />
                              Course Evaluation Results
                            </h5>
                            <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                              <div className="flex flex-col items-center p-2 bg-white rounded">
                                <span className="text-xs text-[#6B7280]">Course Avg</span>
                                <span className="font-bold text-blue-600">{evaluations.trainee_evaluation.course_average}/5</span>
                              </div>
                              <div className="flex flex-col items-center p-2 bg-white rounded">
                                <span className="text-xs text-[#6B7280]">Trainer Avg</span>
                                <span className="font-bold text-green-600">{evaluations.trainee_evaluation.trainer_average}/5</span>
                              </div>
                              <div className="flex flex-col items-center p-2 bg-white rounded">
                                <span className="text-xs text-[#6B7280]">Overall</span>
                                <span className="font-bold text-indigo-600">{evaluations.trainee_evaluation.overall_average}/5</span>
                              </div>
                            </div>
                            <div className="p-2 bg-white rounded mb-3">
                              <span className="text-sm">Would Recommend: </span>
                              <Badge className={evaluations.trainee_evaluation.would_recommend ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {evaluations.trainee_evaluation.would_recommend ? 'Yes ✓' : 'No ✗'}
                              </Badge>
                            </div>
                            {evaluations.trainee_evaluation.what_liked_most && (
                              <div className="p-2 bg-white rounded mb-2">
                                <Label className="text-xs text-[#6B7280]">What they liked most:</Label>
                                <p className="text-sm">{evaluations.trainee_evaluation.what_liked_most}</p>
                              </div>
                            )}
                            {evaluations.trainee_evaluation.recommendations && (
                              <div className="p-2 bg-yellow-50 rounded mb-2">
                                <Label className="text-xs text-[#6B7280]">Recommendations:</Label>
                                <p className="text-sm">{evaluations.trainee_evaluation.recommendations}</p>
                              </div>
                            )}
                            {evaluations.trainee_evaluation.general_comments && (
                              <div className="p-2 bg-white rounded">
                                <Label className="text-xs text-[#6B7280]">General Comments:</Label>
                                <p className="text-sm">{evaluations.trainee_evaluation.general_comments}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Certificate Generation */}
              <div className="border rounded-lg p-4 bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-600" />
                    {language === 'ar' ? 'إنشاء الشهادة' : 'Generate Certificate'}
                  </h4>
                </div>
                
                <p className="text-sm text-[#6B7280] mb-4">
                  {language === 'ar' 
                    ? 'إنشاء شهادة مهنية بعلامة WeDo التجارية للمشارك'
                    : 'Generate a professional WeDo-branded certificate for the participant'}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline"
                    className="border-amber-600 text-amber-700 hover:bg-amber-100"
                    onClick={async () => {
                      try {
                        toast.info(language === 'ar' ? 'جاري إنشاء الشهادة...' : 'Generating certificate...');
                        const response = await fetch(
                          `${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/certificate?certificate_type=attendance`,
                          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
                        );
                        if (!response.ok) throw new Error('Failed to generate certificate');
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `WeDo_Certificate_${selectedEnrollment.full_name?.replace(/\s+/g, '_') || 'Participant'}_Attendance.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                        toast.success(language === 'ar' ? 'تم إنشاء شهادة الحضور' : 'Attendance Certificate downloaded!');
                      } catch (error) {
                        toast.error(language === 'ar' ? 'فشل إنشاء الشهادة' : 'Failed to generate certificate');
                      }
                    }}
                    data-testid="generate-attendance-cert"
                  >
                    <Award className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'شهادة حضور' : 'Certificate of Attendance'}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="border-green-600 text-green-700 hover:bg-green-100"
                    onClick={async () => {
                      try {
                        toast.info(language === 'ar' ? 'جاري إنشاء الشهادة...' : 'Generating certificate...');
                        const response = await fetch(
                          `${API}/admin/confirmed-enrollments/${selectedEnrollment.enrollment_id}/certificate?certificate_type=completion`,
                          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
                        );
                        if (!response.ok) throw new Error('Failed to generate certificate');
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `WeDo_Certificate_${selectedEnrollment.full_name?.replace(/\s+/g, '_') || 'Participant'}_Completion.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                        toast.success(language === 'ar' ? 'تم إنشاء شهادة الإتمام' : 'Completion Certificate downloaded!');
                      } catch (error) {
                        toast.error(language === 'ar' ? 'فشل إنشاء الشهادة' : 'Failed to generate certificate');
                      }
                    }}
                    data-testid="generate-completion-cert"
                  >
                    <CheckCircle className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'شهادة إتمام' : 'Certificate of Completion'}
                  </Button>
                </div>
                
                <p className="text-xs text-[#6B7280] mt-3">
                  {language === 'ar' 
                    ? `سيتم إنشاء الشهادة باسم: ${selectedEnrollment.full_name}`
                    : `Certificate will be generated for: ${selectedEnrollment.full_name}`}
                </p>
              </div>

              {/* Admin Fields */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-[#111827]">{language === 'ar' ? 'إدارة التسجيل' : 'Manage Enrollment'}</h4>
                
                <div className="space-y-2">
                  <Label>{language === 'ar' ? 'الحالة' : 'Status'}</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {language === 'ar' ? status.labelAr : status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'ar' ? 'ملاحظات الإدارة' : 'Admin Notes'}</Label>
                  <Textarea
                    value={editForm.admin_notes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                    rows={3}
                    placeholder={language === 'ar' ? 'ملاحظات داخلية...' : 'Internal notes...'}
                  />
                </div>
              </div>

              <div className="text-sm text-[#6B7280]">
                <p>Enrollment ID: {selectedEnrollment.enrollment_id}</p>
                <p>Request ID: {selectedEnrollment.request_id}</p>
                <p>Created: {formatDate(selectedEnrollment.created_at)}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
            <Button onClick={handleUpdateEnrollment} disabled={updating} className="btn-primary">
              {updating 
                ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
                : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
